import { Grid, IconButton, Paper, Typography, useTheme } from "@mui/material";
import { Box } from "@mui/system";
import React, { useCallback, useEffect } from "react";
import { useDropzone } from "react-dropzone";
import { ImUpload } from "react-icons/im";
import DeleteIcon from "@mui/icons-material/Delete";
import ReactPlayer from "react-player/lazy";

const FileDropZone = ({
  maxFiles,
  accept,
  mode,
  files,
  setFiles,
  showPreview = true,
  error,
}) => {
  const [activeZone, setActiveZone] = React.useState(false);

  const theme = useTheme();
  const { acceptedFiles, getRootProps, getInputProps } = useDropzone({
    onDragEnter: () => {
      setActiveZone(true);
    },
    onDragLeave: () => {
      setActiveZone(false);
    },

    maxFiles: maxFiles,
    multiple: maxFiles > 1,
    accept: accept,
  });

  const removeFile = (file) => {
    acceptedFiles.splice(acceptedFiles.indexOf(file), 1);
    refreshFiles();
  };

  const refreshFiles = useCallback(() => {
    setFiles(
      acceptedFiles.map((file) =>
        Object.assign(file, {
          preview: URL.createObjectURL(file),
        })
      )
    );
  }, [acceptedFiles, setFiles]);

  useEffect(() => {
    refreshFiles();
  }, [acceptedFiles, refreshFiles]);

  const bgColor =
    theme.palette.mode === "light" ? "grey.100" : "background.default";

  return (
    <Box
      display="flex"
      flexDirection="column"
      flexGrow={1}
      sx={{ overflow: "hidden", paddingY: 1 }}
    >
      <Paper
        sx={{
          display: "flex",
          flexGrow: 1,
          flexDirection: "column",
        }}
        elevation={2}
      >
        <Box
          sx={{
            margin: 1,
            borderWidth: 2,
            borderStyle: "dashed",
            borderRadius: 2,
            backgroundColor: bgColor,
            minHeight: 80,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            borderColor: activeZone ? "#eeeeee" : "primary.main",
          }}
          {...getRootProps({ className: "dropzone" })}
        >
          <input {...getInputProps()} />
          <ImUpload
            color="gray"
            size={20}
            style={{ marginRight: 5, marginBottom: 1 }}
          />
          <Typography variant="h6" color="gray">
            Click or drag and drop to upload
          </Typography>
        </Box>
      </Paper>
      {error && (
        <Typography variant="body1" color="error" sx={{ m: 1 }}>
          {error}
        </Typography>
      )}

      {showPreview && (
        <Grid container spacing={2} paddingBottom={2} sx={{ mt: 1 }}>
          {files.map((file) => (
            <Grid item xs={12} md={12} lg={12} key={file.name}>
              <Paper
                sx={{
                  display: "flex",
                  height: mode === "video" ? 150 : 80,
                  flexDirection: "row",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
                elevation={1}
              >
                <Box
                  display="flex"
                  sx={{
                    flexGrow: 1,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    alignItems: "center",
                  }}
                >
                  {mode === "image" && (
                    <img
                      alt=""
                      src={file.preview}
                      style={{
                        width: 80,
                        height: 80,
                        objectFit: "contain",
                        marginLeft: 3,
                      }}
                      onLoad={() => {
                        URL.revokeObjectURL(file.preview);
                      }}
                    />
                  )}

                  {mode === "video" && (
                    <ReactPlayer
                      width={200}
                      height={150}
                      url={file.preview}
                      controls={true}
                      light={false}
                    />
                  )}

                  <Typography mx={2} noWrap>
                    {file.name.length > 10
                      ? `${file.name.slice(0, 30)}...`
                      : file.name}
                  </Typography>
                </Box>
                <IconButton
                  aria-label="delete"
                  color="error"
                  sx={{ flexShrink: 0, mx: 1 }}
                  onClick={() => removeFile(file)}
                >
                  <DeleteIcon />
                </IconButton>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default FileDropZone;
